import { useState } from 'react';
import { displayAddress, type V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { estimateExportBytes, exportBlockFor, exportInspectionAction } from '../../state/inspectionTransfer';
import { Banner, Button, SheetShell, showToast } from '../../ui/components';

const LONG_TOAST_MS = 6000;

// "About 12.4 MB" — a size the inspector can judge a transfer by.
export function approximateSize(bytes: number, locale: string): string {
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString(locale)} KB`;
  return `${(mb < 10 ? Math.round(mb * 10) / 10 : Math.round(mb)).toLocaleString(locale)} MB`;
}

// 「搬到另一台设备」 (Owner 2026-10-20): what the file holds, about how big it is, that this copy becomes view-only,
// and that the file carries the client's details — then the export itself (src/state/inspectionTransfer.ts).
export function MoveOutSheet(props: { inspection: V2Inspection; onClose: () => void; onMoved?: () => void }) {
  const { t, tp, locale } = useT();
  const [busy, setBusy] = useState(false);
  const inspectionId = props.inspection.id;
  const photos = useStore(evidenceStore, (s) => s.evidence.filter((e) => e.inspectionId === inspectionId).length);
  const block = exportBlockFor(inspectionId);

  async function run() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await exportInspectionAction(inspectionId, { prefix: t('move.filePrefix') });
      if (result.kind === 'exported') {
        showToast(t('move.done', { file: result.fileName }), 'success', LONG_TOAST_MS);
        props.onClose();
        props.onMoved?.();
      } else if (result.kind === 'cancelled') showToast(t('move.cancelled'), 'info', LONG_TOAST_MS);
      else if (result.kind === 'blocked') showToast(result.reason === 'aiRunning' ? t('move.blocked.aiRunning') : t('move.blocked.other'), 'info', LONG_TOAST_MS);
      else showToast(t('move.failed', { detail: result.detail }), 'info', LONG_TOAST_MS);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell
      title={t('move.title')}
      subtitle={displayAddress(props.inspection, t('inspection.noAddress'))}
      onClose={props.onClose}
      testId="move-sheet"
      footer={
        <Button icon="drive_file_move" onClick={() => void run()} disabled={busy || block !== null} testId="move-confirm">
          {busy ? t('move.working') : t('move.confirm')}
        </Button>
      }
    >
      <div className="stack">
        {block === 'aiRunning' ? <Banner tone="warning" icon="schedule" title={t('move.blocked.aiRunning')} testId="move-blocked" /> : null}
        <p className="card-body" data-testid="move-body">
          {tp('move.body', photos, { size: approximateSize(estimateExportBytes(inspectionId), locale) })}
        </p>
        <p className="card-note">{t('move.lockNote')}</p>
        <p className="card-note">{t('move.privacyNote')}</p>
      </div>
    </SheetShell>
  );
}
